import React from "react";

/**
 * The playful LemonWire.exe logo. A chunky beveled yellow lemon with a green leaf,
 * connected to an old-school 2-prong electrical plug via a coiled wire.
 * All inline SVG so it scales crisply.
 */
export default function LemonWireLogo({ size = 60, withText = true, className = "" }) {
  const h = size;
  const w = withText ? size * 5 : size * 1.8;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <svg
        viewBox="0 0 110 70"
        width={withText ? h * 1.8 : h * 1.6}
        height={h}
        aria-label="LemonWire logo"
        className="shrink-0"
        style={{ filter: "drop-shadow(2px 3px 0 rgba(0,0,0,0.35))" }}
      >
        <defs>
          <radialGradient id="lw-lemon-body" cx="35%" cy="32%" r="70%">
            <stop offset="0%" stopColor="#FFFDE7" />
            <stop offset="30%" stopColor="#FFF59D" />
            <stop offset="65%" stopColor="#FFEB3B" />
            <stop offset="100%" stopColor="#F9A825" />
          </radialGradient>
          <linearGradient id="lw-leaf" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#A8E063" />
            <stop offset="100%" stopColor="#1F8020" />
          </linearGradient>
          <linearGradient id="lw-plug" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D8D8D8" />
            <stop offset="60%" stopColor="#808080" />
            <stop offset="100%" stopColor="#404040" />
          </linearGradient>
        </defs>

        {/* Coiled wire from lemon top -> plug (black rubber) */}
        <path
          d="M 52 10 C 62 -4, 72 22, 78 10 C 84 -4, 94 22, 104 6"
          stroke="#1A1A1A" strokeWidth="4" fill="none" strokeLinecap="round"
        />
        <path
          d="M 52 10 C 62 -4, 72 22, 78 10 C 84 -4, 94 22, 104 6"
          stroke="#3A3A3A" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.8"
        />

        {/* Plug at right end (2-prong) */}
        <rect x="96" y="0" width="12" height="10" fill="url(#lw-plug)" rx="1.5" />
        <rect x="98" y="-3" width="1.8" height="4" fill="#A0A0A0" />
        <rect x="105" y="-3" width="1.8" height="4" fill="#A0A0A0" />

        {/* Leaf (behind lemon) */}
        <path
          d="M 46 12 Q 60 -2, 72 8 Q 60 6, 50 18 Z"
          fill="url(#lw-leaf)"
          stroke="#0E5A10" strokeWidth="1.2"
        />
        <path d="M 50 12 Q 58 8, 66 7" stroke="#0E5A10" strokeWidth="0.7" fill="none" opacity="0.7" />

        {/* Lemon body */}
        <ellipse cx="42" cy="42" rx="36" ry="26" fill="url(#lw-lemon-body)" stroke="#B87B00" strokeWidth="2" />
        {/* Lemon tips */}
        <ellipse cx="7" cy="42" rx="5" ry="3" fill="#F9A825" />
        <ellipse cx="77" cy="42" rx="5" ry="3" fill="#F9A825" />

        {/* Lemon highlight */}
        <ellipse cx="30" cy="31" rx="14" ry="7" fill="#FFF9C4" opacity="0.85" />
        <ellipse cx="26" cy="29" rx="5" ry="2" fill="#FFFFFF" opacity="0.9" />

        {/* Pixel-style dots on skin */}
        <circle cx="54" cy="52" r="1.1" fill="#C48A00" opacity="0.6" />
        <circle cx="48" cy="58" r="0.9" fill="#C48A00" opacity="0.5" />
        <circle cx="60" cy="44" r="0.9" fill="#C48A00" opacity="0.5" />
        <circle cx="40" cy="56" r="0.7" fill="#C48A00" opacity="0.4" />
      </svg>

      {withText && (
        <div className="flex flex-col leading-none">
          <div
            className="flex items-baseline"
            style={{ fontFamily: '"Pixelify Sans", Tahoma, sans-serif', lineHeight: 1 }}
          >
            <span style={{
              fontSize: h * 0.7,
              color: "#FFEB3B",
              textShadow: "2px 2px 0 #8B6C00, -1px -1px 0 #FFFDE7"
            }}>
              Lemon
            </span>
            <span style={{
              fontSize: h * 0.7,
              color: "#32CD32",
              textShadow: "2px 2px 0 #0F4F0F, -1px -1px 0 #B6FF00"
            }}>
              Wire
            </span>
            <span style={{
              fontSize: h * 0.32,
              color: "#003c9e",
              textShadow: "1px 1px 0 rgba(255,255,255,0.8)",
              fontFamily: '"Share Tech Mono", monospace',
              marginLeft: 4,
            }}>
              .exe
            </span>
          </div>
          <div
            className="mt-0.5 text-[11px] italic"
            style={{ color: "#3D2E00", textShadow: "1px 1px 0 rgba(255,255,255,0.6)" }}
          >
            "No Malware. Just Bangers." &mdash; since 2003
          </div>
        </div>
      )}
    </div>
  );
}
